import React, { useEffect, useRef, useContext, useState, useMemo, useCallback } from 'react';
import { PatientDataContext } from '../../../context/PatientDataContext';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import Breadcrumb from '../../../components/Breadcrumb';
import PADSideNavbar from './components/PADSideNavbar';
import { TriggerGenerateDocumentSection } from '../../../utils/TriggerGenerateDocumentSection';
import { ChevronRightIcon, ChevronLeftIcon, PlusIcon, CheckIcon, CheckCircleIcon } from '@heroicons/react/solid';
import InfoTooltip from '../../../components/InfoTooltip';
import { Tooltip } from 'react-tooltip';
import ModuleCompletionDialog, { CancelButton, GenerateReportButton, SummarySection } from '../../../components/ModuleCompletionDialog';
import GenerateReportProgressDialog from '../../../components/GenerateReportProgressDialog';
import { serverTimestamp } from 'firebase/firestore';
import { buildFirstGeneratedFields, buildFirstCompletedFields, buildPadCompletionFields, appendReportAuditEntry } from '../../../utils/reportAuditHelpers';
import { recordInferredAppointmentDateOnCompletion } from '../../../utils/appointmentDateHelpers';
// import { MicrophoneIcon, StopIcon, QuestionMarkCircleIcon } from '@heroicons/react/solid';
import DocumentPreviewPanel from './components/DocumentPreviewPanel';
import { auth } from '../../../firebase';
import { isPadAdhdOutcomePositive } from '../../../utils/conditionChecks';
import DictationTextArea from '../../../components/DictationTextArea';
import ButtonSpinner from '../../../components/informantManagement/ButtonSpinner';
import { useCanEditStage } from '../../../hooks/useCanEditStage';
import { useSectionExitRegeneration } from '../../../hooks/useSectionExitRegeneration';
import { useAuth } from '../../../hooks/useAuth';
import useWorkflowConfig from '../../../hooks/useWorkflowConfig';
import {
  getPadStageOrder,
  getPadSidebarIndex,
  getPadSidebarSections,
  getPadNavLinks,
  rewriteSubsectionTitleDisplay,
  readIsCarryOutHealthChecksNow,
  readPatientTreatmentPreferences,
  shouldShowHealthChecksGate,
  readHealthChecksGateAnswer,
  HEALTH_CHECKS_GATE_PATH,
  buildPadSidebarUnlockFlags,
  resolveHealthChecksGateAnswer,
  isHealthChecksGateAnswerComplete,
} from '../../../utils/padNavigationUtils';
import {
  buildClearDeferredHealthChecksReportUpdate,
  isHealthChecksGateDeferred,
} from '../../../utils/padHealthChecksReportUtils';

export default function PADDiscussionTopics() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { patientDataModel, updatePatientFirestore, flushPendingUpdates } = useContext(PatientDataContext);
  const { claims } = useAuth();
  // When the AMP stage is disabled for this org (e.g. Well Life Clinic), the
  // Agreed Medication Plan (section 5) is hidden from the PAD sidebar.
  const { isAgreedMedicationPlanEnabled, isPadTreatmentFirst = false, isAppointmentDatesEnabled = false } = useWorkflowConfig(patientDataModel, claims);
  const [hasChanges, setHasChanges] = useState(false);

  // Determines whether this user is the clinician assigned to the PAD stage.
  // When false the entire page renders in read-only mode.
  const canEdit = useCanEditStage(patientDataModel, 'PAD');

  // Registers dirty state with ProformaNavigationGuardContext and provides
  // unmount-based regeneration as a safety net for unmanaged exits (breadcrumb,
  // top nav, browser back). markRegenHandled() is called in handleSectionCompletion
  // to prevent double-firing when the managed Next/Sidebar path is used.
  const { markRegenHandled } = useSectionExitRegeneration({
    patientId,
    stageTitle: 'Post-Assessment Discussion',
    sectionTitle: 'Strategies and Recommendations',
    hasChanges,
    canEdit,
    patientDataModel,
  });

  // Add form state management
  const [formState, setFormState] = useState({});

  // Speech recognition states
  // const [activeRecording, setActiveRecording] = useState(null);
  // const [recognition, setRecognition] = useState(null);
  const textAreaRefs = useRef({});

  // // Speech recognition handlers
  // const handleRecording = (subsectionId) => {
  //   if (!('webkitSpeechRecognition' in window)) {
  //     alert('Speech recognition not supported in this browser.');
  //     return;
  //   }

  //   if (recognition) {
  //     recognition.stop();
  //     return;
  //   }

  //   const newRecognition = new window.webkitSpeechRecognition();
  //   newRecognition.continuous = true;
  //   newRecognition.interimResults = true;
  //   newRecognition.lang = 'en-GB';

  //   newRecognition.onresult = (event) => {
  //     let finalTranscript = notes[subsectionId] || '';
  //     for (let i = event.resultIndex; i < event.results.length; i++) {
  //       let transcript = event.results[i][0].transcript.trim();
  //       if (event.results[i].isFinal) {
  //         transcript = ' ' + transcript.charAt(0).toUpperCase() + transcript.slice(1) + '.';
  //         finalTranscript += transcript;
  //       }
  //     }
  //     handleNotesChange(subsectionId, finalTranscript);
  //   };

  //   newRecognition.onend = () => {
  //     setActiveRecording(null);
  //     setRecognition(null);

  //     // Simulate focus and blur to trigger onBlur
  //     const currentTextArea = textAreaRefs.current[subsectionId];
  //     if (currentTextArea) {
  //       currentTextArea.focus();
  //       setTimeout(() => {
  //         currentTextArea.blur();
  //       }, 0);
  //     }
  //   };

  //   newRecognition.start();
  //   setRecognition(newRecognition);
  //   setActiveRecording(subsectionId);

  //   // Focus the text area when recording starts
  //   const currentTextArea = textAreaRefs.current[subsectionId];
  //   if (currentTextArea) {
  //     currentTextArea.focus();
  //   }
  // };

  // Panel states 
  const [notes, setNotes] = useState({});

  // Constants
  const currentStageTitle = 'Post-Assessment Discussion';
  const currentSectionNumber = 2;
  const currentSectionTitle = 'Strategies and Recommendations';

  // Ordered stage list for the clinician PAD — controls sidebar order, display
  // numbers, and prev/next links. Treatment-first (SLaM) swaps sections 2/3.
  const padStageOrder = useMemo(
    () => getPadStageOrder({ isPadTreatmentFirst, isAgreedMedicationPlanEnabled }),
    [isPadTreatmentFirst, isAgreedMedicationPlanEnabled]
  );

  // Read the "Carry out health checks now?" answer from the stable Treatment
  // Options Firestore path — used for sidebar unlock AND the gate UI in
  // treatment-first mode. Local optimistic override comes from gateAnswer state.
  const isCarryOutHealthChecksNowFromServer = useMemo(
    () => readIsCarryOutHealthChecksNow(patientDataModel),
    [patientDataModel]
  );

  // In treatment-first mode, the health-checks gate UI lives on THIS page
  // (Strategies), so we maintain local optimistic state for immediate feedback.
  const [gateAnswer, setGateAnswer] = useState(null);
  const isCarryOutHealthChecksNow = gateAnswer !== null
    ? gateAnswer === 'Yes'
    : isCarryOutHealthChecksNowFromServer;

  // Navigation links derived from the ordered stage list. In treatment-first,
  // Strategies (key '2') becomes the gate-owner page: the helper returns
  // showFinish when health checks are declined, or Next → Health Checks
  // when accepted.
  const padNav = useMemo(
    () => getPadNavLinks('2', padStageOrder, { isCarryOutHealthChecksNow }),
    [padStageOrder, isCarryOutHealthChecksNow]
  );
  const prevSectionLink = padNav.prevLink || '/proforma/post-assessment-discussion/review-assessment';
  const prevSectionTitle = padNav.prevTitle || 'Review of Assessment';
  const nextSectionLink = padNav.nextLink || '/proforma/post-assessment-discussion/treatment-options';
  const nextSectionTitle = padNav.nextTitle || 'Treatment Options';

  // Get the section data using useMemo
  const sectionData = useMemo(() => {
    return patientDataModel?.dataModel?.['5_postAssessmentDiscussion']?.sections?.[currentSectionNumber] || {};
  }, [patientDataModel]);

  // MDT Plan selections — used to show "Added to plan in MDT" badge on items
  // the clinician flagged during the Diagnostic Decision.
  const mdtPlanStrategies = useMemo(() => (
    patientDataModel?.dataModel?.['4_diagnosticDecision']?.mdtPlan?.toDiscussStrategies || {}
  ), [patientDataModel]);

  // Get subsections data
  const subsections = useMemo(() => {
    return sectionData?.subsections || {};
  }, [sectionData]);

  // Add a ref to track initial load
  const isInitialLoad = useRef(true);

  useEffect(() => {
    // Only run on initial load
    if (!isInitialLoad.current) return;
    if (!sectionData?.subsections) return;

    const initialFormState = {};
    const initialNotes = {};

    Object.entries(sectionData.subsections).forEach(([subsectionId, subsection]) => {
      // Initialize notes if they exist
      if (subsection?.clinicianNotes) {
        initialNotes[subsectionId] = subsection.clinicianNotes;
      }
      
      // Initialize form state for questions
      if (subsection?.questions) {
        Object.entries(subsection.questions).forEach(([questionId, question]) => {
          if (question?.firestoreFieldName) {
            initialFormState[question.firestoreFieldName] = {
              value: question?.answer?.value || []
            };
          }
        });
      }
    });

    setFormState(initialFormState);
    setNotes(initialNotes);
    
    // Mark initial load as complete
    isInitialLoad.current = false;
  }, [sectionData]); // Keep the dependency array to ensure we have the data  

  const handleMultipleSelect = async (subsectionId, fieldName, option, isSelected) => {
    if (!canEdit) return;
    try {
      // Get current selections
      const currentSelections = formState[fieldName]?.value || [];
      let newSelections;

      if (isSelected) {
        // Remove the option if it's already selected
        newSelections = currentSelections.filter(opt => opt !== option);
      } else {
        // Add the new option
        newSelections = [...currentSelections, option];
      }

      // Create update object
      const updateObject = {
        value: newSelections
      };

      // Update local state first
      setFormState(prev => ({
        ...prev,
        [fieldName]: updateObject
      }));

      // Find the question index in the subsection
      const questionIndex = Object.entries(sectionData.subsections[subsectionId].questions)
        .find(([_, q]) => q.firestoreFieldName === fieldName)?.[0];

      if (!questionIndex) {
        throw new Error('Question not found');
      }

      // Uses nested-object format (matching CI/SSI convention) so the write
      // can be deep-merged with other section writes when batched together.
      await updatePatientFirestore(patientId, {
        '5_postAssessmentDiscussion': {
          sections: {
            [currentSectionNumber]: {
              subsections: {
                [subsectionId]: {
                  questions: {
                    [questionIndex]: { answer: updateObject }
                  }
                }
              }
            }
          }
        }
      });
      setHasChanges(true);
    } catch (error) {
      console.error('Error updating Firestore:', error);
      // Revert local state if Firestore update fails
      setFormState(prev => ({
        ...prev,
        [fieldName]: {
          value: formState[fieldName]?.value || []
        }
      }));
    }
  };

  // Add the notes handlers
  const handleNotesChange = (subsectionId, value) => {
    if (!canEdit) return;
    setNotes(prev => ({
      ...prev,
      [subsectionId]: value
    }));
    setHasChanges(true);
  };

  // Persists the current note value to Firestore on blur.
  //
  // IMPORTANT: this handler intentionally does NOT call setHasChanges(true).
  // Dirty-tracking is handled exclusively by handleNotesChange (the onChange
  // handler). The blur handler's sole job is data persistence. Combining the
  // two caused false-positive dirty checks: the DictationTextArea's unmount
  // safety-net can fire onBlur even when no text was edited, which would
  // incorrectly flag the page as dirty and trigger the navigation guard.
  const handleNotesBlur = (subsectionId) => {
    if (!canEdit || !subsectionId) return;

    // Skip the write if the note hasn't changed from what Firestore has.
    const currentValue = notes[subsectionId] ?? '';
    const storedValue = subsections[subsectionId]?.clinicianNotes || '';
    if (currentValue === storedValue) return;

    try {
      updatePatientFirestore(patientId, {
        '5_postAssessmentDiscussion': {
          sections: {
            [currentSectionNumber]: {
              subsections: {
                [subsectionId]: {
                  clinicianNotes: notes[subsectionId] || null
                }
              }
            }
          }
        }
      });
    } catch (error) {
      console.error('Error updating Firestore:', error);
    }
  };  

  // ── Health-checks gate (treatment-first only) ──────────────────────────
  // When the PAD runs Treatment-first (SLaM), the "Carry out health checks
  // now?" Yes/No question is relocated from the Treatment Options page to
  // here as a synthetic final panel. This writes to the SAME Firestore path
  // (Treatment Options section 3 → subsection 3 → question 2) so the rest
  // of the pipeline is unaffected.

  // Read the patient's treatment preferences to decide if the gate is visible.
  const treatmentPreferences = useMemo(
    () => readPatientTreatmentPreferences(patientDataModel),
    [patientDataModel]
  );
  const showHealthChecksGatePanel = isPadTreatmentFirst && shouldShowHealthChecksGate(treatmentPreferences);

  // Initialise the local gate answer from the server value so the UI reflects
  // a previously-stored Yes OR "No - defer" without needing a user interaction
  // first. Only the first matching option is used (selectOne).
  const gateInitialisedRef = useRef(false);
  useEffect(() => {
    if (gateInitialisedRef.current || !isPadTreatmentFirst) return;
    const stored = readHealthChecksGateAnswer(patientDataModel);
    if (stored.length > 0) {
      setGateAnswer(stored[0]);
    }
    gateInitialisedRef.current = true;
  }, [isPadTreatmentFirst, patientDataModel]);

  // Read the gate question definition from the data model for rendering.
  const healthChecksGateQuestion = useMemo(() => {
    if (!isPadTreatmentFirst) return null;
    return patientDataModel?.dataModel?.['5_postAssessmentDiscussion']?.sections?.[
      HEALTH_CHECKS_GATE_PATH.sectionKey
    ]?.subsections?.[HEALTH_CHECKS_GATE_PATH.subsectionId]?.questions?.[
      HEALTH_CHECKS_GATE_PATH.questionId
    ] || null;
  }, [patientDataModel, isPadTreatmentFirst]);

  // Handler for the health-checks gate Yes/No selection. Writes to the TRT
  // section Firestore path (section '3', subsection '3', question '2') so
  // the answer persists in the canonical location.
  //
  // Intentionally does NOT call setHasChanges(true): the gate answer is not
  // Strategies content and is never fed into Strategies LLM prompts. Marking
  // Strategies dirty here would trigger a needless Strategies regeneration on
  // exit whenever the clinician only touched this relocated gate. Local
  // gateAnswer state still drives Finish vs Go-to-Health-Checks immediately,
  // and the write itself is flushed via updatePatientFirestore / flushPendingUpdates.
  const handleHealthChecksGateSelect = useCallback(async (selectedOption) => {
    if (!canEdit) return;
    const currentSelections = gateAnswer === selectedOption ? [] : [selectedOption];
    const newValue = currentSelections.length > 0 ? currentSelections[0] : null;

    setGateAnswer(newValue);

    try {
      // When the clinician leaves Yes (No - defer, or clears the answer), wipe
      // any Health Checks / AMP paragraphs generated during an earlier Yes
      // visit. Otherwise RD_PAD and the CAR keep showing those stale sections
      // even though Health Checks is no longer part of the discussion.
      const clearReportUpdate = isHealthChecksGateDeferred(currentSelections)
        ? buildClearDeferredHealthChecksReportUpdate(patientDataModel)
        : null;

      await updatePatientFirestore(patientId, {
        '5_postAssessmentDiscussion': {
          sections: {
            [HEALTH_CHECKS_GATE_PATH.sectionKey]: {
              subsections: {
                [HEALTH_CHECKS_GATE_PATH.subsectionId]: {
                  questions: {
                    [HEALTH_CHECKS_GATE_PATH.questionId]: {
                      answer: { value: currentSelections }
                    }
                  }
                }
              }
            }
          }
        },
        ...(clearReportUpdate || {}),
      });
    } catch (error) {
      console.error('Error writing health-checks gate:', error);
      setGateAnswer(null);
    }
  }, [canEdit, gateAnswer, patientId, updatePatientFirestore, patientDataModel]);

  // Treatment-first: when the synthetic gate panel is visible, block Finish /
  // Go to Health Checks until the clinician picks Yes or No - defer.
  const resolvedHealthChecksGateAnswer = useMemo(
    () => resolveHealthChecksGateAnswer(gateAnswer, patientDataModel),
    [gateAnswer, patientDataModel],
  );
  const isHealthChecksGateComplete =
    !showHealthChecksGatePanel ||
    isHealthChecksGateAnswerComplete(resolvedHealthChecksGateAnswer);

  // Compute the display number for the gate panel heading (e.g. "3.4" when
  // Strategies is stage 3 and there are 3 normal subsections before it).
  const gateSubsectionNumber = useMemo(() => {
    if (!isPadTreatmentFirst) return null;
    const existingCount = Object.keys(subsections).length;
    return existingCount + 1;
  }, [isPadTreatmentFirst, subsections]);

  const SECTIONS_PAD = useMemo(() => {
    const isDiagnosisADHD = isPadAdhdOutcomePositive(patientDataModel);
    const isBeginningMedicationNow = isCarryOutHealthChecksNow && patientDataModel?.dataModel?.['5_postAssessmentDiscussion']?.sections?.['4']?.subsections?.['4']?.questions?.['2']?.answer?.value?.includes("Yes");

    return getPadSidebarSections(
      patientDataModel?.dataModel?.['5_postAssessmentDiscussion']?.sections,
      padStageOrder,
      buildPadSidebarUnlockFlags({
        patientDataModel,
        isPadTreatmentFirst,
        isDiagnosisADHD,
        isCarryOutHealthChecksNow,
        isBeginningMedicationNow,
      }),
    );
  }, [patientDataModel, padStageOrder, isCarryOutHealthChecksNow, isPadTreatmentFirst]);

  const sendSectionUpdates = useCallback((newUpdates) => {
    updatePatientFirestore(patientId, {
      '5_postAssessmentDiscussion': {
        sections: {
          [currentSectionNumber]: newUpdates
        }
      }
    });
  }, [patientId, currentSectionNumber, updatePatientFirestore]);


  const handleSectionCompletion = useCallback((patientId, currentStageTitle, currentSectionTitle, hasChanges) => {
    if (!canEdit) return Promise.resolve();

    markRegenHandled();

    const currentSectionData = patientDataModel?.dataModel?.['5_postAssessmentDiscussion']?.sections?.[currentSectionNumber];
    const isAlreadyCompleted = currentSectionData?.status === 'completed';
    const sectionsAlreadyGenerated = patientDataModel?.dataModel?.['6_manageReports']?.sections?.['4']?.stageSectionsGenerated;
    const isCurrentSectionGenerated = sectionsAlreadyGenerated?.includes(currentSectionTitle) || false;

    const needsStatusWrite = !isAlreadyCompleted;
    const needsGeneration = hasChanges || !isCurrentSectionGenerated;

    if (!needsStatusWrite && !needsGeneration) return Promise.resolve();

    if (needsStatusWrite) {
      sendSectionUpdates({ status: 'completed', progress: 100 });
    }

    const dataReady = flushPendingUpdates({ noTimeout: true });

    if (needsGeneration) {
      TriggerGenerateDocumentSection(patientId, currentStageTitle, currentSectionTitle, [], dataReady);
    }

    return dataReady;
  }, [canEdit, patientDataModel, currentSectionNumber, sendSectionUpdates, flushPendingUpdates, markRegenHandled]);


  // Finish/completion dialog state — only relevant when treatment-first or
  // when the page is the final one (strategies-first with health checks declined
  // would never reach DIS as a finish page, but in treatment-first DIS becomes
  // the gate owner so "Finish" can show here).
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleFinish = async () => {
    if (!isHealthChecksGateComplete) return;
    setIsSaving(true);
    try {
      await handleSectionCompletion(patientId, currentStageTitle, currentSectionTitle, hasChanges);
      setIsDialogOpen(true);
    } catch (error) {
      console.error("Error completing section:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleGenerateReport = () => {
    setIsDialogOpen(false);
    setIsGenerating(true);
  };

  const handleCancelGeneration = () => {
    setIsGenerating(false);
  };

  const handleNextPageButtonClick = () => {
    const manageReports = patientDataModel?.dataModel?.['6_manageReports'];
    const firstCompletedFields = buildFirstCompletedFields(patientDataModel?.dataModel?.['5_postAssessmentDiscussion']);

    updatePatientFirestore(patientId, {
      '5_postAssessmentDiscussion': buildPadCompletionFields(patientDataModel, firstCompletedFields),
      '6_manageReports': {
        padReportGenerated: true,
        padReportGeneratedAt: serverTimestamp(),
        padReportGeneratedBy: auth.currentUser.uid,
        padReportGeneratedByName: auth.currentUser.displayName,
        ...buildFirstGeneratedFields('pad', manageReports),
      }
    });

    appendReportAuditEntry(patientId, {
      module: 'pad',
      eventType: 'generated',
      isFirstOccurrence: !manageReports?.padReportFirstGeneratedAt,
    });

    // Inferred appointment audit on first completion (fire-and-forget; does not write appointmentDates)
    recordInferredAppointmentDateOnCompletion({
      patientId,
      moduleKey: 'pad',
      didWriteFirstCompleted: Object.keys(firstCompletedFields).length > 0,
      enabled: isAppointmentDatesEnabled,
    });

    navigate(`/patient/${patientId}/documents`);
  };

  const [isNavigating, setIsNavigating] = useState(false);

  const isExitCTADisabled = isSaving || isNavigating || !isHealthChecksGateComplete;
  const showHealthChecksGateTooltip =
    showHealthChecksGatePanel && canEdit && !isHealthChecksGateComplete;

  const handleNextSection = useCallback(() => {
    if (isNavigating || !isHealthChecksGateComplete) return;
    document.activeElement?.blur();
    setIsNavigating(true);
    handleSectionCompletion(patientId, currentStageTitle, currentSectionTitle, hasChanges).then(() => {
      navigate(`/patient/${patientId}${nextSectionLink}`);
    });
  }, [isNavigating, isHealthChecksGateComplete, patientId, navigate, nextSectionLink, hasChanges, handleSectionCompletion]);

  const navigateToSection = useCallback((index) => {
    if (isNavigating) return;
    document.activeElement?.blur();
    setIsNavigating(true);
    handleSectionCompletion(patientId, currentStageTitle, currentSectionTitle, hasChanges).then(() => {
      const section = SECTIONS_PAD[index];
      if (section.route) {
        navigate(`/patient/${patientId}${section.route}`);
      }
    });
  }, [isNavigating, patientId, navigate, SECTIONS_PAD, hasChanges, handleSectionCompletion]);

  const handleBackNavigation = useCallback(() => {
    if (isNavigating) return;
    document.activeElement?.blur();
    setIsNavigating(true);
    handleSectionCompletion(patientId, currentStageTitle, currentSectionTitle, hasChanges).then(() => {
      navigate(`/patient/${patientId}${prevSectionLink}`);
    });
  }, [isNavigating, patientId, navigate, prevSectionLink, hasChanges, handleSectionCompletion]);

  const breadcrumbTuples = [
    
    ["Patient Overview", `/patient/${patientId}`],
    [currentStageTitle],
    [currentSectionTitle]
  ];

  return (
    <Layout activePage="dashboard">
      <Breadcrumb tupleList={breadcrumbTuples} />
      <div className="flex">
        <PADSideNavbar
          sections={SECTIONS_PAD}
          currentSectionIndex={getPadSidebarIndex(padStageOrder, '2')}
          navigateToSection={navigateToSection}
          isNavigating={isSaving || isNavigating}
        />
        <div className="flex-grow p-8">

          {/* Navigation Buttons */}
          <div className="flex justify-between items-center mb-6">
            <button
              onClick={handleBackNavigation}
              disabled={isSaving || isNavigating}
              className={`flex items-center py-2 px-4 mb-2 rounded-md shadow-md text-gray-900 border border-gray-300 transition-all duration-300 ${(isSaving || isNavigating) ? 'opacity-60 cursor-not-allowed' : 'hover:bg-gray-50'}`}
              aria-label="Back to Previous Section"
            >
              {(isSaving || isNavigating)
                ? <ButtonSpinner className="w-5 h-5 mr-2" />
                : <ChevronLeftIcon className="w-5 h-5 mr-2" aria-hidden="true" />}
              <span className="font-bold">{(isSaving || isNavigating) ? 'Loading...' : `Back to ${prevSectionTitle}`}</span>
            </button>

            {/* Show Next when the helper provides a nextLink; otherwise show
                Finish when treatment-first and the gate is declined. */}
            {!padNav.showFinish ? (
              <div>
                <button
                  onClick={handleNextSection}
                  disabled={isExitCTADisabled}
                  data-tooltip-id="health-checks-gate-tooltip-top"
                  className={`flex items-center py-2 px-4 mb-2 rounded-md shadow-md transition-all duration-300 ease-in-out ml-auto ${
                    isExitCTADisabled
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'hover:bg-violet-800 bg-violet-900 text-white'
                  }`}
                >
                  <span className="font-bold">{(isSaving || isNavigating) ? 'Loading...' : `Go to ${nextSectionTitle}`}</span>
                  {(isSaving || isNavigating)
                    ? <ButtonSpinner className="w-5 h-5 ml-2" />
                    : <ChevronRightIcon className="w-5 h-5 ml-2" aria-hidden="true" />}
                </button>
                {showHealthChecksGateTooltip && (
                  <Tooltip
                    id="health-checks-gate-tooltip-top"
                    place="top"
                    className="z-50 text-xs font-semibold"
                    style={{
                      maxWidth: '250px',
                      whiteSpace: 'normal',
                      backgroundColor: 'rgba(34, 34, 34, 1)',
                      borderRadius: '15px',
                      color: '#fff',
                    }}
                  >
                    Please answer whether to carry out health checks now before proceeding
                  </Tooltip>
                )}
              </div>
            ) : (
              canEdit && (
                <div>
                  <button
                    onClick={handleFinish}
                    disabled={isExitCTADisabled}
                    data-tooltip-id="health-checks-gate-tooltip-top"
                    className={`flex items-center py-2 px-4 mb-2 rounded-md shadow-md transition-all duration-300 ease-in-out ml-auto ${
                      isExitCTADisabled
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'hover:bg-violet-800 bg-violet-900 text-white'
                    }`}
                  >
                    <span className="font-bold">{isSaving ? "Saving Progress..." : "Finish Post-Assessment Discussion"}</span>
                    {isSaving ? <ButtonSpinner className="w-5 h-5 ml-2" /> : <CheckCircleIcon className="w-5 h-5 ml-2" aria-hidden="true" />}
                  </button>
                  {showHealthChecksGateTooltip && (
                    <Tooltip
                      id="health-checks-gate-tooltip-top"
                      place="top"
                      className="z-50 text-xs font-semibold"
                      style={{
                        maxWidth: '250px',
                        whiteSpace: 'normal',
                        backgroundColor: 'rgba(34, 34, 34, 1)',
                        borderRadius: '15px',
                        color: '#fff',
                      }}
                    >
                      Please answer whether to carry out health checks now before proceeding
                    </Tooltip>
                  )}
                </div>
              )
            )}
          </div>

          {/* Section Title */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">{padNav.displayNumber}. {currentSectionTitle}</h2>
          </div>

          <DocumentPreviewPanel
            patientId={patientId}
            clinicianId={auth.currentUser?.uid || ''}
          />

          {/* Subsections */}
          {Object.entries(subsections).map(([subsectionId, subsection]) => {
            // Skip if subsection is null or undefined
            if (!subsection) return null;

            return (
              <div key={subsectionId} className="p-8 my-8 bg-gray-50 rounded-2xl border border-gray-200">
                <h3 className="text-left text-slate-700 text-xl font-semibold mb-6">
                  {rewriteSubsectionTitleDisplay(subsection.subsectionTitleDisplay, padNav.displayNumber)}
                </h3>

                <div className="space-y-4">
                  {Object.entries(subsection.questions || {}).map(([questionId, question]) => {
                    // Skip if question is null or undefined
                    if (!question) return null;

                    return (
                      <div key={questionId} className="mb-4 flex items-start">
                        <div className="w-96 flex flex-col items-start">
                          <div className="flex items-center gap-2">
                            {question.tooltipText ? (
                              <InfoTooltip 
                                content={question.tooltipText} 
                                label={`Information about ${question.questionTitle}`}
                                maxWidth="300px"
                                className=""
                              />
                            ) : (
                              <div className="w-4 h-4"></div>
                            )}
                            <span className="font-semibold">{question.questionTitle}</span>
                          </div>
                          {mdtPlanStrategies[question.firestoreFieldName] === true && (
                            <span className="ml-6 mt-0.5 block w-fit text-[10px] text-violet-600 bg-violet-50 border border-violet-100 rounded px-1.5 py-px font-medium leading-tight">
                              Added to plan in MDT
                            </span>
                          )}
                        </div>

                        <div className="ml-5">
                          <ul className="items-center flex flex-row gap-4">
                            {(question.options || []).map((option) => {
                              const currentSelections = formState[question.firestoreFieldName]?.value || [];
                              const isSelected = currentSelections.includes(option);

                              return (
                                <li
                                  key={option}
                                  onClick={() => canEdit && handleMultipleSelect(
                                    subsectionId,
                                    question.firestoreFieldName,
                                    option,
                                    isSelected
                                  )}
                                  className={`
                                  flex-1 p-2 w-64
                                  rounded-lg transition-all duration-300 ease-in-out
                                  text-center
                                  flex flex-row items-center justify-center gap-2
                                  ${!canEdit
                                      ? 'cursor-not-allowed opacity-75'
                                      : 'cursor-pointer group'
                                  }
                                  ${isSelected
                                      ? 'bg-violet-600 border-2 border-violet-800 font-semibold shadow-lg transform scale-105'
                                      : canEdit
                                        ? 'bg-white hover:bg-gray-50 border-2 border-dashed border-gray-300 hover:border-gray-400 shadow-sm hover:shadow-md'
                                        : 'bg-white border-2 border-dashed border-gray-300 shadow-sm'
                                    }
                                `}
                                >
                                  <div className={`transition-all duration-300 ease-in-out ${isSelected ? 'text-white' : 'text-gray-400 group-hover:text-gray-600'}`}>
                                    {isSelected ? (
                                      <CheckIcon className="w-4 h-4" />
                                    ) : (
                                      <PlusIcon className="w-4 h-4" />
                                    )}
                                  </div>
                                  <span className={`font-semibold transition-all duration-300 ease-in-out ${isSelected ? 'text-white' : 'text-gray-700 group-hover:text-gray-900'}`}>
                                    {option}
                                  </span>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      </div>
                    );

                  })}
                </div>

                {/* Clinician Notes Box */}
                {subsection.hasClinicianNotesBox && (
                  <div className="text-left mt-6">
                    <h4 className="text-gray-700 text-sm font-semibold mb-2">{subsection.textBoxSubtitle}</h4>
                    <DictationTextArea
                      ref={(el) => textAreaRefs.current[subsectionId] = el}
                      value={notes[subsectionId] || ''}
                      onChange={(val) => handleNotesChange(subsectionId, val)}
                      onBlur={() => handleNotesBlur(subsectionId)}
                      canEdit={canEdit}
                    />
                  </div>
                )}
              </div>
            )
          })}
          
          {/* Synthetic health-checks gate panel (treatment-first only).
              When Treatment runs before Strategies, the "Carry out health checks
              now?" question is shown here as the final panel so the clinician can
              decide whether to proceed to Health Checks or finish the PAD. */}
          {showHealthChecksGatePanel && healthChecksGateQuestion && (
            <div className="p-8 my-8 bg-gray-50 rounded-2xl border border-gray-200">
              <h3 className="text-left text-slate-700 text-xl font-semibold mb-6">
                {padNav.displayNumber}.{gateSubsectionNumber} {healthChecksGateQuestion.questionTitle}
              </h3>
              <div className="mb-4 flex items-start">
                <div className="w-96 flex items-center gap-2">
                  <div className="w-4 h-4"></div>
                  <h4 className="text-left font-semibold text-gray-700">
                    {healthChecksGateQuestion.questionTitle}
                  </h4>
                </div>
                <div className="ml-5">
                  <ul className="items-center flex flex-row gap-4">
                    {(healthChecksGateQuestion.options || []).map((option) => {
                      // Prefer local optimistic state; fall back to the first
                      // stored server value so a prior "No - defer" stays selected.
                      const serverAnswer = readHealthChecksGateAnswer(patientDataModel)[0] || null;
                      const currentAnswer = gateAnswer ?? serverAnswer;
                      const isSelected = currentAnswer === option;

                      return (
                        <li
                          key={option}
                          onClick={() => canEdit && handleHealthChecksGateSelect(option)}
                          className={`
                            flex-1 p-2 w-64
                            rounded-lg transition-all duration-300 ease-in-out
                            text-center
                            flex flex-row items-center justify-center gap-2
                            ${!canEdit
                              ? 'cursor-not-allowed opacity-75'
                              : 'cursor-pointer group'
                            }
                            ${isSelected
                              ? 'bg-violet-600 border-2 border-violet-800 font-semibold shadow-lg transform scale-105'
                              : canEdit
                                ? 'bg-white hover:bg-gray-50 border-2 border-dashed border-gray-300 hover:border-gray-400 shadow-sm hover:shadow-md'
                                : 'bg-white border-2 border-dashed border-gray-300 shadow-sm'
                            }
                          `}
                        >
                          <div className={`transition-all duration-300 ease-in-out ${isSelected ? 'text-white' : 'text-gray-400 group-hover:text-gray-600'}`}>
                            {isSelected ? (
                              <CheckIcon className="w-4 h-4" />
                            ) : (
                              <PlusIcon className="w-4 h-4" />
                            )}
                          </div>
                          <span className={`font-semibold transition-all duration-300 ease-in-out ${isSelected ? 'text-white' : 'text-gray-700 group-hover:text-gray-900'}`}>
                            {option}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            </div>
          )}

           {/* Navigation Buttons */}
           <div className="flex justify-end items-center mt-8 pt-4 border-t border-gray-200">
            {!padNav.showFinish ? (
              <div>
                <button
                  onClick={handleNextSection}
                  disabled={isExitCTADisabled}
                  data-tooltip-id="health-checks-gate-tooltip-bottom"
                  className={`flex items-center py-2 px-4 mb-2 rounded-md shadow-md transition-all duration-300 ease-in-out ml-auto ${
                    isExitCTADisabled
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'hover:bg-violet-800 bg-violet-900 text-white'
                  }`}
                >
                  <span className="font-bold">{(isSaving || isNavigating) ? 'Loading...' : `Go to ${nextSectionTitle}`}</span>
                  {(isSaving || isNavigating)
                    ? <ButtonSpinner className="w-5 h-5 ml-2" />
                    : <ChevronRightIcon className="w-5 h-5 ml-2" aria-hidden="true" />}
                </button>
                {showHealthChecksGateTooltip && (
                  <Tooltip
                    id="health-checks-gate-tooltip-bottom"
                    place="top"
                    className="z-50 text-xs font-semibold"
                    style={{
                      maxWidth: '250px',
                      whiteSpace: 'normal',
                      backgroundColor: 'rgba(34, 34, 34, 1)',
                      borderRadius: '15px',
                      color: '#fff',
                    }}
                  >
                    Please answer whether to carry out health checks now before proceeding
                  </Tooltip>
                )}
              </div>
            ) : (
              canEdit && (
                <div>
                  <button
                    onClick={handleFinish}
                    disabled={isExitCTADisabled}
                    data-tooltip-id="health-checks-gate-tooltip-bottom"
                    className={`flex items-center py-2 px-4 mb-2 rounded-md shadow-md transition-all duration-300 ease-in-out ml-auto ${
                      isExitCTADisabled
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'hover:bg-violet-800 bg-violet-900 text-white'
                    }`}
                  >
                    <span className="font-bold">{isSaving ? "Saving Progress..." : "Finish Post-Assessment Discussion"}</span>
                    {isSaving ? <ButtonSpinner className="w-5 h-5 ml-2" /> : <CheckCircleIcon className="w-5 h-5 ml-2" aria-hidden="true" />}
                  </button>
                  {showHealthChecksGateTooltip && (
                    <Tooltip
                      id="health-checks-gate-tooltip-bottom"
                      place="top"
                      className="z-50 text-xs font-semibold"
                      style={{
                        maxWidth: '250px',
                        whiteSpace: 'normal',
                        backgroundColor: 'rgba(34, 34, 34, 1)',
                        borderRadius: '15px',
                        color: '#fff',
                      }}
                    >
                      Please answer whether to carry out health checks now before proceeding
                    </Tooltip>
                  )}
                </div>
              )
            )}
          </div>

        </div>

        <ModuleCompletionDialog
          open={isDialogOpen && !isGenerating}
          onOpenChange={setIsDialogOpen}
          title="Post-Assessment Discussion Complete"
          width={600}
          actions={
            <>
              <CancelButton onClick={() => setIsDialogOpen(false)} />
              <GenerateReportButton onClick={handleGenerateReport} />
            </>
          }
        >
          <SummarySection title="Summary:">
            The Post-Assessment Discussion has been completed. Would you like to generate the report?
          </SummarySection>
        </ModuleCompletionDialog>

        <GenerateReportProgressDialog
          reportStage="Post-Assessment Discussion"
          patientId={patientId}
          clinicianId={auth.currentUser?.uid || ''}
          handleNextPageButtonClick={handleNextPageButtonClick}
          isGenerating={isGenerating}
          onCancel={handleCancelGeneration}
        />
      </div>
    </Layout>
  );
}